import React, { useState } from 'react';
import { Webhook, Plus, Play, Pause, Send, List, Trash2 } from 'lucide-react';
import {
  useWebhooks,
  useUpdateWebhook,
  useDeleteWebhook,
  useTestWebhook,
} from '../../../hooks';
import CreateWebhookModal from './CreateWebhookModal';
import WebhookLogsModal from './WebhookLogsModal';
import { WebhooksTabSkeleton } from '../shared/Skeleton';

export default function WebhooksTab() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);

  const { data, isLoading } = useWebhooks();
  const updateWebhook = useUpdateWebhook();
  const deleteWebhook = useDeleteWebhook();
  const testWebhook = useTestWebhook();

  const webhooks = data?.items || [];

  const handleToggleActive = async (webhook) => {
    await updateWebhook.mutateAsync({
      webhookId: webhook.id,
      data: { active: !webhook.active },
    });
  };

  const handleDelete = async (webhookId) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      await deleteWebhook.mutateAsync(webhookId);
    }
  };

  const handleTest = async (webhookId) => {
    await testWebhook.mutateAsync(webhookId);
  };

  const handleViewLogs = (webhook) => {
    setSelectedWebhook(webhook);
    setShowLogsModal(true);
  };

  function formatDate(dateStr) {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function getStatusClass(status) {
    switch (status) {
      case 'active': return 'workbench-badge-success';
      case 'paused': return 'workbench-badge-muted';
      case 'degraded': return 'workbench-badge-warning';
      case 'failing': return 'workbench-badge-error';
      default: return 'workbench-badge-muted';
    }
  }

  return (
    <div className="webhooks-container">
      <div className="webhooks-header">
        <div>
          <h2>Webhooks</h2>
          <p>Receive real-time event notifications</p>
        </div>
        <button className="workbench-btn workbench-btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={16} />
          Create Webhook
        </button>
      </div>

      {isLoading ? (
        <WebhooksTabSkeleton />
      ) : webhooks.length === 0 ? (
        <div className="workbench-empty">
          <Webhook size={48} />
          <h3>No webhooks configured yet</h3>
          <p>Create a webhook to receive real-time event notifications</p>
        </div>
      ) : (
        <div className="workbench-card">
          <table className="workbench-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>URL</th>
                <th>Events</th>
                <th>Status</th>
                <th>Last Delivery</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((webhook) => (
                <tr key={webhook.id}>
                  <td className="webhook-name">{webhook.name}</td>
                  <td>
                    <code className="workbench-code-inline webhook-url" title={webhook.url}>
                      {webhook.url}
                    </code>
                  </td>
                  <td>
                    <span className="workbench-badge workbench-badge-muted">
                      {webhook.events.length} events
                    </span>
                  </td>
                  <td>
                    <span className={`workbench-badge ${getStatusClass(webhook.status)}`}>
                      {webhook.status}
                    </span>
                  </td>
                  <td>{formatDate(webhook.last_success_at)}</td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="action-btn"
                        title={webhook.active ? 'Pause' : 'Activate'}
                        onClick={() => handleToggleActive(webhook)}
                        disabled={updateWebhook.isPending}
                      >
                        {webhook.active ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button
                        className="action-btn"
                        title="Test webhook"
                        onClick={() => handleTest(webhook.id)}
                        disabled={testWebhook.isPending}
                      >
                        <Send size={14} />
                      </button>
                      <button
                        className="action-btn"
                        title="View logs"
                        onClick={() => handleViewLogs(webhook)}
                      >
                        <List size={14} />
                      </button>
                      <button
                        className="action-btn danger"
                        title="Delete"
                        onClick={() => handleDelete(webhook.id)}
                        disabled={deleteWebhook.isPending}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreateWebhookModal onClose={() => setShowCreateModal(false)} />
      )}

      {showLogsModal && selectedWebhook && (
        <WebhookLogsModal
          webhook={selectedWebhook}
          onClose={() => { setShowLogsModal(false); setSelectedWebhook(null); }}
        />
      )}
    </div>
  );
}
